<script>
import constants from '@/constants';

export default {
    data() {
        return {
            debounceTimers: {},
            localFilters: {
                userId: null,
                email: '',
                engagementTier: constants.FILTER_ALL,
                recencyStatus: constants.FILTER_ALL,
            },
        };
    },
    computed: {
        filters() {
            return this.$store.getters.USER_ANALYTICS_FILTERS;
        },
        engagementTiers() {
            return [
                { value: constants.FILTER_ALL, label: 'All Tiers', hint: '' },
                ...constants.ENGAGEMENT_TIERS,
            ];
        },
        recencyStatuses() {
            return [
                {
                    value: constants.FILTER_ALL,
                    label: 'All Statuses',
                    hint: '',
                },
                ...constants.RECENCY_STATUSES,
            ];
        },
        activeFilterCount() {
            let count = 0;
            if (this.localFilters.userId) count++;
            if (this.localFilters.email) count++;
            if (this.localFilters.engagementTier !== constants.FILTER_ALL)
                count++;
            if (this.localFilters.recencyStatus !== constants.FILTER_ALL)
                count++;
            return count;
        },
    },
    watch: {
        filters: {
            handler(newFilters) {
                this.localFilters = { ...newFilters };
            },
            deep: true,
        },
    },
    mounted() {
        this.localFilters = { ...this.filters };
    },
    methods: {
        debouncedApplyFilters(timerKey) {
            clearTimeout(this.debounceTimers[timerKey]);
            this.debounceTimers[timerKey] = setTimeout(() => {
                this.applyFilters();
            }, constants.DEBOUNCE_DELAY_MS);
        },
        applyFilters() {
            this.$store.commit('SET_USER_ANALYTICS_FILTERS', this.localFilters);
            this.$store.commit('SET_USER_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadUserAnalytics');
        },
        clearFilters() {
            this.localFilters = {
                userId: null,
                email: '',
                engagementTier: constants.FILTER_ALL,
                recencyStatus: constants.FILTER_ALL,
            };
            this.$store.commit('RESET_USER_ANALYTICS_FILTERS');
            this.$store.dispatch('loadUserAnalytics');
        },
        getEngagementTierHint(value) {
            const tier = this.engagementTiers.find((t) => t.value === value);
            return tier ? tier.hint : '';
        },
        getRecencyStatusHint(value) {
            const status = this.recencyStatuses.find((s) => s.value === value);
            return status ? status.hint : '';
        },
    },
};
</script>

<template>
    <div class="filters_container">
        <div class="filters_header">
            <label class="filter_title">Filters</label>
            <button
                v-if="activeFilterCount > 0"
                @click="clearFilters"
                class="clear_button"
            >
                Clear All ({{ activeFilterCount }})
            </button>
        </div>

        <div class="filters_grid">
            <div class="filter_field">
                <label class="mb_5">User ID:</label>
                <input
                    v-model.number="localFilters.userId"
                    type="number"
                    placeholder="e.g., 123"
                    @input="debouncedApplyFilters('userId')"
                />
            </div>

            <div class="filter_field">
                <label class="mb_5">Email:</label>
                <input
                    v-model="localFilters.email"
                    type="text"
                    placeholder="Search by email..."
                    @input="debouncedApplyFilters('email')"
                />
            </div>

            <div class="filter_field">
                <label class="mb_5">Engagement Tier:</label>
                <select
                    v-model="localFilters.engagementTier"
                    @change="applyFilters"
                    :title="getEngagementTierHint(localFilters.engagementTier)"
                >
                    <option
                        v-for="tier in engagementTiers"
                        :key="tier.value"
                        :value="tier.value"
                        :title="tier.hint"
                    >
                        {{ tier.label }}
                    </option>
                </select>
                <span
                    v-if="
                        localFilters.engagementTier !== engagementTiers[0].value
                    "
                    class="filter_hint"
                >
                    {{ getEngagementTierHint(localFilters.engagementTier) }}
                </span>
            </div>

            <div class="filter_field">
                <label class="mb_5">Recency Status:</label>
                <select
                    v-model="localFilters.recencyStatus"
                    @change="applyFilters"
                    :title="getRecencyStatusHint(localFilters.recencyStatus)"
                >
                    <option
                        v-for="status in recencyStatuses"
                        :key="status.value"
                        :value="status.value"
                        :title="status.hint"
                    >
                        {{ status.label }}
                    </option>
                </select>
                <span
                    v-if="
                        localFilters.recencyStatus !== recencyStatuses[0].value
                    "
                    class="filter_hint"
                >
                    {{ getRecencyStatusHint(localFilters.recencyStatus) }}
                </span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.filters_container {
    background: var(--background-color);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
}

.filters_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.filter_title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-color);
}

.clear_button {
    background: transparent;
    border: 1px solid var(--border-color);
    color: var(--text-color);
    padding: 5px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s ease;
}

.clear_button:hover {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
}

.filters_grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
}

.filter_field {
    display: flex;
    flex-direction: column;
}

.filter_field label {
    font-size: 13px;
    color: var(--text-color);
}

.filter_field input,
.filter_field select {
    padding: 8px 10px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
}

.filter_field input:focus,
.filter_field select:focus {
    outline: none;
    border-color: var(--primary-color);
}

.mb_5 {
    margin-bottom: 5px;
}

.filter_hint {
    font-size: 11px;
    color: #6b7280;
    margin-top: 4px;
    font-style: italic;
}
</style>
